// ════════════════════════════════════════════════════════════════════════════
// 🛡️ <Can action="..."> — Phase 5 Declarative RBAC gate for React UI
// ════════════════════════════════════════════════════════════════════════════
// Usage:
//   <Can action="transaction:void"><VoidButton /></Can>
//   <Can action="cashier:create" fallback={<p>Ask your manager</p>}>…</Can>
//   <Can action="dayend:run" session={cashier}>…</Can>   ← explicit session override
//
// When the current cashier does not have the requested permission, the children
// are not rendered. `fallback` prop (optional) is rendered instead.
//
// Backend enforces the same matrix via Bearer JWT on every request — this
// component is UI polish only, not a security boundary.
// ════════════════════════════════════════════════════════════════════════════

import React from 'react';
import { can, usePermissions, type CashierSessionLite } from '../../utils/usePermissions';

interface CanProps {
  action: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  /** Optional explicit session override — preferred when the consumer already
   *  holds the cashier state (e.g. POSDashboard.cashier). */
  session?: CashierSessionLite | null;
}

export const Can: React.FC<CanProps> = ({ action, children, fallback = null, session }) => {
  const hook = usePermissions();
  const effectiveSession = session !== undefined ? session : hook.session;
  return <>{can(effectiveSession, action) ? children : fallback}</>;
};

export default Can;
